import * as React from 'react';
import { AutoCompleteOptionList } from './AutoCompleteItemList';
import styled from 'styled-components';

interface IState {
  isFocus: boolean;
  hasValue: boolean;
  placeHolderContent: any;
  placeHolderPropose: string;
  autoCompletedValue: boolean;
  filteredList: Array<any>;
  selectedIndex: number;
}

export interface IAutoCompleteProps extends React.InputHTMLAttributes<any> {
  color?: string;
  label: string;
  hasError?: boolean;
  errorMessage?: string | null | undefined | {};
  innerRef?: any;
  listToAutoComplete: Array<any>;
  onValueFound: any;
}

interface IAutoCompleteLabelProps {
  themeColor: string;
  isFocus: boolean;
  hasValue: boolean;
  hasError?: boolean;
  disabled?: boolean;
}

interface IAutoPlaceHolderProps {
  themeColor: string;
  isFocus: boolean;
}

const StyledAutoCompleteContainer = styled.div`
  margin: 8px;
  display: inline-flex;
  position: relative;
  flex-direction: column;
  box-sizing: border-box;
`;

const StyledAutoComplete = styled.input<{
  hasError?: boolean,
  isFocus: boolean,
  themeColor: string
}>
`
  font-size: ${props => props.theme.defaultFontSize};
  font-family: ${props => props.theme.fontFamily};
  box-sizing: border-box;
  height: 22px;

  padding-bottom: 4px;

  border: 0;
  border-bottom: ${props => {
    if (props.hasError)
      return `1px solid ${props.theme.errorColor}`;
    else if (props.isFocus)
      return `2px solid ${props.theme[props.themeColor]}`;
    else
      return `1px solid ${props.theme.greyColor}`;
  }};

  transition: border 150ms ease-out;

  outline:0;

  &:disabled {
    background: transparent;
    border-bottom: 1px dashed ${props => props.theme.greyColor};
  }
`;

const ErrorLabel = styled.label`
  font-size: calc(${props => props.theme.defaultFontSize} * 0.85);
  font-family: ${props => props.theme.fontFamily};
  color: ${props => props.theme.errorColor};

  min-height: 22px;

  position: relative;
`;

const StyledAutoCompleteLabel = styled.label<IAutoCompleteLabelProps>`
  font-size: ${props => props.theme.defaultFontSize};
  font-family: ${props => props.theme.fontFamily};

  color: ${props => {
    if (props.disabled)
      return props.theme.disabledColor;
    else if (props.hasError)
      return props.theme.errorColor;
    else if (props.isFocus)
      return props.theme[props.themeColor];
    else return props.theme.greyTextColor;
  }};

  position: absolute;
  left: 0;
  transform: ${props => props.isFocus || props.hasValue ? 'translate3d(0, -100%, 0);font-size: 85%;' : ''};

  pointer-events: none;

  transition: font-size 200ms, transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms;
`;

const StyledPlaceHolder = styled.label<IAutoPlaceHolderProps>`
  font-size: ${props => props.theme.defaultFontSize};
  font-family: ${props => props.theme.fontFamily};
  box-sizing: border-box;
  height: 22px;
  padding-bottom: 4px;
  border: 0;
  position: absolute;
  left: 0;
  opacity: 0.5;
   ${props => {
    if (props.isFocus)
      return 'top:-2px';
    else
      return 'top:-2px';
  }};

  transition: position 150ms ease-out;
  outline:0;

`;

/**
 * AutoComplete
 */
/** @component */
export class AutoComplete extends React.Component<Partial<IAutoCompleteProps>, IState> {

  constructor(props: IAutoCompleteProps) {
    super(props);
    this.state = {
      hasValue: Boolean(this.props.value),
      isFocus: false,
      placeHolderContent: '',
      placeHolderPropose: '',
      autoCompletedValue: false,
      filteredList: this.props.listToAutoComplete || [],
      selectedIndex: 0
    };
  }

  focus = (e: React.FocusEvent<HTMLInputElement>) => {
    this.setState({ isFocus: true });
    this.change(e);
  }

  blur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;

    this.setState({
      isFocus: false,
      placeHolderContent: '',
      placeHolderPropose: '',
      hasValue: Boolean(value)
    });
  }

  onItemSelected = (e: any) => {
    const { value } = e.target;

    this.props.onValueFound(value, e);
    this.setState({
      placeHolderContent: '',
      placeHolderPropose: '',
      autoCompletedValue: true,
      selectedIndex: 0
    });
  }

  change = (e: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value.length >= 2 && this.props.listToAutoComplete) {
      const autoCompleteList = this.props.listToAutoComplete.filter(p => p.label.toLowerCase().startsWith(e.target.value.toLowerCase()));
      if (autoCompleteList.length > 0) {
        const label = autoCompleteList[0].label;
        const text = <>
          <span style={{ opacity: 0 }}>
            {e.target.value}
          </span>
          <span >
            {label.substring(e.target.value.length)}
          </span>
        </>;
        this.setState({
          placeHolderPropose: autoCompleteList[0].value,
          placeHolderContent: text,
          autoCompletedValue: false,
          filteredList: autoCompleteList,
          selectedIndex: 0
        });
      } else {
        this.setState({
          placeHolderContent: '',
          autoCompletedValue: false,
          filteredList: autoCompleteList,
          selectedIndex: 0
        }
        );
      }
    } else {
      this.setState({
        placeHolderContent: '',
        autoCompletedValue: false,
        filteredList: [],
        selectedIndex: 0
      });
    }
  }

  keyDown = (e: any) => {
    if (e.key == 'Enter' && this.state.placeHolderPropose != '') {
      e.target.value = this.state.placeHolderPropose;
      this.props.onValueFound(this.state.placeHolderPropose, e);
      this.setState({
        placeHolderContent: '',
        placeHolderPropose: '',
        selectedIndex: 0,
        autoCompletedValue: true,
        filteredList: []
      });
    } else if (e.key == 'ArrowDown' && this.state.filteredList.length > 0) {
      let selectedIndex = (
        this.state.selectedIndex >= this.state.filteredList.length - 1
          ? 0
          : (this.state.selectedIndex) + 1
      );
      const text = <>
        <span style={{ opacity: 0 }}>
          {e.target.value}
        </span>
        <span >
          {this.state.filteredList[selectedIndex].label.substring(e.target.value.length)}
        </span>
      </>;

      this.setState({
        selectedIndex: selectedIndex,
        autoCompletedValue: false,
        placeHolderPropose: this.state.filteredList[selectedIndex].value,
        placeHolderContent: text

      });
    } else if (e.key == 'ArrowUp' && this.state.filteredList.length > 0) {
      let selectedIndex = (
        this.state.selectedIndex <= 0
          ? this.state.filteredList.length - 1
          : (this.state.selectedIndex) - 1
      );
      let text = <>
        <span style={{ opacity: 0 }}>
          {e.target.value}
        </span>
        <span >
          {this.state.filteredList[selectedIndex].label.substring(e.target.value.length)}
        </span>
      </>;
      this.setState({
        selectedIndex: selectedIndex,
        autoCompletedValue: false,
        placeHolderPropose: this.state.filteredList[selectedIndex].value,
        placeHolderContent: text
      });
    }
  }

  render() {
    const {
      label,
      style,
      color,
      hasError,
      errorMessage,
      id,
      ...props } = this.props;
    const ErrorDOM = hasError ? <div>{errorMessage}</div> : null;
    const { onValueFound, ...propsFoward } = props;

    return (
      <StyledAutoCompleteContainer
        style={style}
        {...propsFoward}
      >

        <StyledPlaceHolder
          htmlFor={id}
          isFocus={this.state.isFocus}
          themeColor={color!}
        >
          {this.state.placeHolderContent}
        </StyledPlaceHolder>

        <StyledAutoCompleteLabel
          htmlFor={id}
          isFocus={this.state.isFocus}
          hasValue={this.state.hasValue}
          themeColor={color!}
          hasError={hasError}
          disabled={props.disabled}
          hidden={props.hidden}
        >
          {label}
        </StyledAutoCompleteLabel>
        <StyledAutoComplete
          {...propsFoward}
          id={id}
          themeColor={color!}
          hasError={hasError}
          onFocus={this.focus}
          onBlur={this.blur}
          onChange={this.change}
          onKeyDown={this.keyDown}
          isFocus={this.state.isFocus}
          autoComplete='off'
        />
        <AutoCompleteOptionList
          optionList={this.state.filteredList}
          focus={this.state.isFocus}
          themeColor={color!}
          onItemSelected={this.onItemSelected}
          selectedItem={
            this.state.filteredList[this.state.selectedIndex] != undefined
              ? this.state.filteredList[this.state.selectedIndex].value
              : -1
          }
        />
        <ErrorLabel>{ErrorDOM}</ErrorLabel>
      </StyledAutoCompleteContainer>
    );
  }

  static defaultProps = {
    color: 'secondaryColor'
  };
}
